import type { Ref } from 'vue';
import type { PatternCell, PatternCoordinates, PatternRail } from '~/utils/auth-pattern';
import { useMediaQuery } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { cellKey, findPath, getCell, LAST_ENTRY_KEY, reachableCells, START_CELL } from '~/utils/auth-pattern';

export const WALK_TIMING = {
	initialDelay: 1600,
	idleStep: 2550,
	pointerStep: 1000,
	keyboardStep: 80,
	settleAfterLeave: 1500,
	pointerRecheck: 250,
	travelStep: 120,
	resumeDelay: 80,
} as const;

/** Keeps an unattended wander visibly long, rather than nudging the highlight to a neighbour. */
const MIN_WANDER_DISTANCE = 3;

const ARROW_STEPS: Record<string, PatternCoordinates> = {
	ArrowUp: { row: -1, column: 0 },
	ArrowDown: { row: 1, column: 0 },
	ArrowLeft: { row: 0, column: -1 },
	ArrowRight: { row: 0, column: 1 },
};

export function useAuthPatternWalk(svg: Ref<SVGSVGElement | null>) {
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

	const current = ref<PatternCoordinates>({ ...START_CELL });
	const target = ref<PatternCoordinates>({ ...START_CELL });
	const isEntering = ref(true);
	/** The highlight is jumping slots to ride its rail across the loop seam, so it must not cross-fade. */
	const isCarrying = ref(false);
	const highlightedKey = computed(() => cellKey(current.value.row, current.value.column));

	/** A pointer or the keyboard is driving the highlight, so the unattended wander stays off. */
	let isAttended = false;
	/** A click asked the highlight to come over, so it steps at the travel cadence until it lands. */
	let isTravelling = false;
	let moveTimer: ReturnType<typeof setTimeout> | undefined;
	let lastPointer: { x: number; y: number } | undefined;
	let pointerRecheckTimer: ReturnType<typeof setInterval> | undefined;

	function clearMoveTimer() {
		if (moveTimer === undefined) return;
		clearTimeout(moveTimer);
		moveTimer = undefined;
	}

	function stepDelay(): number {
		if (isTravelling) return WALK_TIMING.travelStep;
		return isAttended ? WALK_TIMING.pointerStep : WALK_TIMING.idleStep;
	}

	function schedule(delay = stepDelay()) {
		clearMoveTimer();
		if (reducedMotion.value) return;
		moveTimer = setTimeout(tick, delay);
	}

	function isAtTarget(): boolean {
		return current.value.row === target.value.row && current.value.column === target.value.column;
	}

	function setTarget(coordinates: PatternCoordinates, travelling = false) {
		target.value = { row: coordinates.row, column: coordinates.column };
		isTravelling = travelling;
	}

	function setRandomTarget() {
		const possibilities = reachableCells.filter((cell) => {
			const distance = Math.abs(cell.row - current.value.row) + Math.abs(cell.column - current.value.column);
			return distance >= MIN_WANDER_DISTANCE;
		});

		const next = possibilities[Math.floor(Math.random() * possibilities.length)];
		if (!next) return;
		setTarget(next);
	}

	function advanceOneCell(): boolean {
		const [next] = findPath(current.value, target.value);
		if (!next) return false;
		current.value = next;
		return true;
	}

	function tick() {
		if (!advanceOneCell() && !isAttended) {
			setRandomTarget();
			advanceOneCell();
		}

		if (isAtTarget()) isTravelling = false;

		schedule();
	}

	/**
	 * The mouth painted topmost under the pointer. Mouths are long diagonals that overlap their
	 * neighbours, so the nearest cell centre is often a different mouth than the one the pointer is on.
	 */
	function cellUnderPointer(point: { x: number; y: number }): PatternCell | undefined {
		const element = document.elementFromPoint(point.x, point.y)?.closest<SVGGElement>('[data-pattern-cell]');
		if (!element || !svg.value?.contains(element)) return;
		return getCell(Number(element.dataset.row), Number(element.dataset.column));
	}

	/** Hovering highlights the mouth under the pointer straight away; gaps and walled-off mouths keep the current one. */
	function highlightUnderPointer() {
		if (!lastPointer) return;
		const hovered = cellUnderPointer(lastPointer);
		if (!hovered) return;

		clearMoveTimer();
		setTarget(hovered);
		current.value = { row: hovered.row, column: hovered.column };
	}

	function stopPointerRecheck() {
		if (pointerRecheckTimer === undefined) return;
		clearInterval(pointerRecheckTimer);
		pointerRecheckTimer = undefined;
	}

	function followPointer(event: PointerEvent) {
		lastPointer = { x: event.clientX, y: event.clientY };
		highlightUnderPointer();
	}

	function handlePointerEnter(event: PointerEvent) {
		isAttended = true;
		followPointer(event);

		// The rails keep sliding under a still pointer, so the hovered mouth changes without any pointer event.
		stopPointerRecheck();
		pointerRecheckTimer = setInterval(highlightUnderPointer, WALK_TIMING.pointerRecheck);
	}

	function handlePointerLeave() {
		isAttended = false;
		lastPointer = undefined;
		stopPointerRecheck();
		setRandomTarget();
		schedule(WALK_TIMING.settleAfterLeave);
	}

	function selectCell(cell: PatternCell) {
		if (!cell.reachable) return;

		// Glide to the clicked cell so the click reads as a destination rather than a teleport.
		setTarget(cell, true);

		if (reducedMotion.value) current.value = { ...target.value };
		else schedule();
	}

	function handleKeydown(event: KeyboardEvent) {
		const step = ARROW_STEPS[event.key];
		if (!step) return;

		const next = getCell(target.value.row + step.row, target.value.column + step.column);
		if (!next) return;

		event.preventDefault();
		isAttended = true;
		setTarget(next);

		if (reducedMotion.value) current.value = { ...target.value };
		else schedule(WALK_TIMING.keyboardStep);
	}

	function handleBlur() {
		isAttended = false;
		setRandomTarget();
		schedule(WALK_TIMING.settleAfterLeave);
	}

	/**
	 * A rail snaps back one slot each time its loop restarts. Moving the highlight one slot the other way
	 * keeps it on the same mouth, and the walk then steps it back toward its target as usual.
	 */
	function handleRailIteration(rail: PatternRail) {
		if (current.value.row !== rail.row) return;

		isCarrying.value = true;
		current.value = { row: rail.row, column: current.value.column + rail.direction };

		requestAnimationFrame(() => requestAnimationFrame(() => {
			isCarrying.value = false;
		}));
	}

	function handleEntryAnimationEnd(cell: PatternCell) {
		if (cell.key === LAST_ENTRY_KEY) isEntering.value = false;
	}

	watch(reducedMotion, (shouldReduceMotion) => {
		if (shouldReduceMotion) {
			isEntering.value = false;
			clearMoveTimer();
			return;
		}

		setRandomTarget();
		schedule(WALK_TIMING.resumeDelay);
	});

	onMounted(() => {
		if (reducedMotion.value) isEntering.value = false;
		setRandomTarget();
		schedule(WALK_TIMING.initialDelay);
	});

	onBeforeUnmount(() => {
		clearMoveTimer();
		stopPointerRecheck();
	});

	return {
		highlightedKey,
		isEntering,
		isCarrying,
		followPointer,
		handlePointerEnter,
		handlePointerLeave,
		handleKeydown,
		handleBlur,
		selectCell,
		handleRailIteration,
		handleEntryAnimationEnd,
	};
}
